import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import {
  AssistantRuntimeProvider,
  ThreadPrimitive,
  useThread,
} from "@assistant-ui/react"
import type { MessageState } from "@assistant-ui/react"
import { isSameDay, startOfDay } from "date-fns"
import { ChevronDown, Loader2, Sparkles } from "lucide-react"
import { useFeatureFlagEnabled } from "posthog-js/react"
import posthog from "posthog-js"
import { useTranslation } from "react-i18next"
import type { Chat } from "@/reducers/chatsSlice"
import { cn } from "@/lib/utils"
import DayMarker from "@/components/DayMarker"
import { useAppSelector } from "@/reducers/hooks"
import { selectMessagesFromChat } from "@/reducers/messagesSliceSelectors"
import { selectLiveTurn } from "@/reducers/agentPipelineSlice"
import { Button } from "@/components/ui/button"
import { extractVhostFromJid } from "@/jidUtils"
import useChatMessageFetcher from "@/hooks/useChatMessageFetcher"
import { useAIChatRuntime } from "./useAIChatRuntime"
import { AIChatMessage } from "./AIChatMessage"
import { useAgentPromptSuggestions } from "./useAgentPromptSuggestions"
import { AgentWelcome } from "./AgentWelcome"
import { AgentCapabilities } from "./AgentCapabilities"
import {
  ENGLISH_TOGGLE_VHOSTS,
  translatePromptSuggestions,
  useFtueT,
} from "./agentFtueEnglish"

const SCROLL_TOP_THRESHOLD = 8
const ANCHOR_STABILIZE_IDLE_MS = 120
const SCROLLING_IDLE_MS = 180
const EMPTY_MESSAGES: never[] = []

type ScrollAnchorSnapshot = {
  messageId?: string
  topOffset?: number
  scrollHeight: number
  scrollTop: number
  messageCount: number
}

const getScrollAnchorSnapshot = (
  viewport: HTMLDivElement,
  messageCount: number,
): ScrollAnchorSnapshot => {
  const viewportTop = viewport.getBoundingClientRect().top
  const messageElements = Array.from(
    viewport.querySelectorAll<HTMLElement>("[data-message-id]"),
  )
  const anchorElement =
    messageElements.find((element) => {
      return element.getBoundingClientRect().bottom > viewportTop
    }) ?? messageElements[0]

  return {
    messageId: anchorElement?.dataset.messageId,
    topOffset: anchorElement
      ? anchorElement.getBoundingClientRect().top - viewportTop
      : undefined,
    scrollHeight: viewport.scrollHeight,
    scrollTop: viewport.scrollTop,
    messageCount,
  }
}

const restoreScrollFromAnchor = (
  viewport: HTMLDivElement,
  snapshot: ScrollAnchorSnapshot,
) => {
  const anchorElement = Array.from(
    viewport.querySelectorAll<HTMLElement>("[data-message-id]"),
  ).find((element) => element.dataset.messageId === snapshot.messageId)

  if (anchorElement != null && snapshot.topOffset != null) {
    const viewportTop = viewport.getBoundingClientRect().top
    const currentTopOffset =
      anchorElement.getBoundingClientRect().top - viewportTop
    viewport.scrollTop += currentTopOffset - snapshot.topOffset
    return
  }

  viewport.scrollTop =
    snapshot.scrollTop + (viewport.scrollHeight - snapshot.scrollHeight)
}

function AIChatMessageRow({
  message,
}: {
  message: Pick<MessageState, "index" | "createdAt">
}) {
  const threadMessages = useThread((state) => state.messages)
  const previousMessage = threadMessages[message.index - 1]
  const shouldRenderDayMarker =
    previousMessage != null &&
    !isSameDay(message.createdAt, previousMessage.createdAt)

  return (
    <>
      {shouldRenderDayMarker && (
        <DayMarker date={startOfDay(message.createdAt)} />
      )}
      <AIChatMessage />
    </>
  )
}

type AIChatViewProps = {
  chat: Chat
  isRoomSyncFinished: boolean
  onSendMessage: (text: string) => void
  onSelectPrompt?: (text: string) => void
}

function AIChatFloatingDayMarker({
  date,
  isScrolling,
  isFetchingOlderMessages,
}: {
  date?: Date
  isScrolling: boolean
  isFetchingOlderMessages: boolean
}) {
  if (!date) return null

  return (
    <div
      data-testid="ai-chat-floating-day-marker"
      className="pointer-events-none absolute inset-x-0 z-10 flex justify-center px-4 transition-opacity duration-500"
      style={{
        top: isFetchingOlderMessages ? 44 : 12,
        opacity: isScrolling ? 1 : 0,
      }}
    >
      <DayMarker date={startOfDay(date)} />
    </div>
  )
}

export function AIChatView({
  chat,
  isRoomSyncFinished,
  onSendMessage,
  onSelectPrompt,
}: AIChatViewProps) {
  const runtime = useAIChatRuntime(chat, onSendMessage)
  const messagesSelector = useMemo(
    () => selectMessagesFromChat(chat.jid),
    [chat.jid],
  )
  const messages = useAppSelector(messagesSelector) ?? EMPTY_MESSAGES
  // FTUE in the anonymous chat is temporarily disabled — flip this to true to
  // re-enable it (anonymous users aren't identified in PostHog so the flag
  // can't target them; this is the always-on switch for anonymous mode).
  const FTUE_ENABLED_IN_ANONYMOUS = false
  // Fall back to the synchronous cached flag value so the FTUE doesn't flash in
  // a tick late (the hook is undefined until a post-mount effect resolves it).
  const aiSuggestionsFlag =
    useFeatureFlagEnabled("ai-suggestions") ??
    posthog.isFeatureEnabled("ai-suggestions")
  const ftueEnabled =
    (FTUE_ENABLED_IN_ANONYMOUS && window.anonymousXMPP === true) ||
    aiSuggestionsFlag === true
  const vhost = useMemo(() => extractVhostFromJid(chat.jid), [chat.jid])
  // Demo-only PL/EN switch at the bottom of the agent room. English mode
  // translates the whole FTUE (hero, cards, suggestions) locally — the
  // backend keeps serving Polish.
  const languageToggleAvailable =
    ftueEnabled && ENGLISH_TOGGLE_VHOSTS.includes(vhost)
  const [english, setEnglish] = useState(false)
  const englishFtue = languageToggleAvailable && english
  const polishFtue = languageToggleAvailable && !english
  const { t } = useTranslation()
  const tFtue = useFtueT(englishFtue, polishFtue)
  const assistantJid = useMemo(() => `transchat@${vhost}/transchat`, [vhost])
  const suggestions = useAgentPromptSuggestions(
    ftueEnabled ? assistantJid : null,
  )
  const { popular, groups } = useMemo(
    () => (englishFtue ? translatePromptSuggestions(suggestions) : suggestions),
    [englishFtue, suggestions],
  )
  const isEmptyChat = messages.length === 0
  const showPromptPill =
    ftueEnabled && !isEmptyChat && (popular.length > 0 || groups.length > 0)
  const isAgentResponding = useAppSelector(
    (state) => selectLiveTurn(state, chat.jid) !== undefined,
  )
  const [showCapabilities, setShowCapabilities] = useState(false)
  // Reset the inline panel when switching chats.
  useEffect(() => setShowCapabilities(false), [chat.jid])
  const handleSelectPrompt = useCallback(
    (text: string) => {
      onSelectPrompt?.(text)
      setShowCapabilities(false)
    },
    [onSelectPrompt],
  )
  const { onFetchMoreMessages } = useChatMessageFetcher(chat, messages)
  const viewportRef = useRef<HTMLDivElement>(null)
  // The inline capabilities panel renders at the bottom of the thread; when it
  // opens, scroll it into view so it never lands below the fold.
  useEffect(() => {
    if (!showCapabilities) return
    const vp = viewportRef.current
    if (!vp) return
    requestAnimationFrame(() => {
      // `scrollTo` is unavailable in jsdom; guard so tests don't throw.
      if (typeof vp.scrollTo === "function") {
        vp.scrollTo({ top: vp.scrollHeight, behavior: "smooth" })
      } else {
        vp.scrollTop = vp.scrollHeight
      }
    })
  }, [showCapabilities])
  const requestedCountRef = useRef(-1)
  const hasMoreOlderMessagesRef = useRef(true)
  const pendingScrollRestoreRef = useRef<ScrollAnchorSnapshot | null>(null)
  const anchorStabilizeTimeoutRef = useRef<number | null>(null)
  const scrollingTimeoutRef = useRef<number | null>(null)
  const [isFetchingOlderMessages, setIsFetchingOlderMessages] = useState(false)
  const [activeScrollAnchor, setActiveScrollAnchor] =
    useState<ScrollAnchorSnapshot | null>(null)
  const [isScrolling, setIsScrolling] = useState(false)
  const [topVisibleMessageId, setTopVisibleMessageId] = useState<string | null>(
    null,
  )
  const messageDateById = useMemo(() => {
    return new Map(
      messages.map((message) => [message.id, new Date(message.timestamp)]),
    )
  }, [messages])
  const topVisibleDate =
    topVisibleMessageId != null
      ? messageDateById.get(topVisibleMessageId)
      : undefined

  useEffect(() => {
    requestedCountRef.current = -1
    hasMoreOlderMessagesRef.current = true
    pendingScrollRestoreRef.current = null
    setActiveScrollAnchor(null)
    setIsFetchingOlderMessages(false)
    setIsScrolling(false)
    setTopVisibleMessageId(null)
  }, [chat.jid])

  useEffect(() => {
    return () => {
      if (anchorStabilizeTimeoutRef.current != null) {
        window.clearTimeout(anchorStabilizeTimeoutRef.current)
      }
      if (scrollingTimeoutRef.current != null) {
        window.clearTimeout(scrollingTimeoutRef.current)
      }
    }
  }, [])

  const updateTopVisibleMessage = useCallback(() => {
    const viewport = viewportRef.current

    if (!viewport) return

    const viewportTop = viewport.getBoundingClientRect().top
    const messageElements = Array.from(
      viewport.querySelectorAll<HTMLElement>("[data-message-id]"),
    )
    const topVisibleMessage =
      messageElements.find((element) => {
        return element.getBoundingClientRect().bottom > viewportTop
      }) ?? messageElements[0]

    setTopVisibleMessageId(topVisibleMessage?.dataset.messageId ?? null)
  }, [])

  const scheduleAnchorRelease = useCallback(() => {
    if (anchorStabilizeTimeoutRef.current != null) {
      window.clearTimeout(anchorStabilizeTimeoutRef.current)
    }

    anchorStabilizeTimeoutRef.current = window.setTimeout(() => {
      setActiveScrollAnchor(null)
      anchorStabilizeTimeoutRef.current = null
    }, ANCHOR_STABILIZE_IDLE_MS)
  }, [])

  const restoreActiveScrollAnchor = useCallback(() => {
    const viewport = viewportRef.current

    if (!viewport || !activeScrollAnchor) return

    restoreScrollFromAnchor(viewport, activeScrollAnchor)
    updateTopVisibleMessage()
    scheduleAnchorRelease()
  }, [activeScrollAnchor, scheduleAnchorRelease, updateTopVisibleMessage])

  useLayoutEffect(() => {
    const pendingRestore = pendingScrollRestoreRef.current

    if (!pendingRestore) return

    if (messages.length > pendingRestore.messageCount) {
      setActiveScrollAnchor(pendingRestore)
      pendingScrollRestoreRef.current = null
      return
    }

    if (!isFetchingOlderMessages) {
      pendingScrollRestoreRef.current = null
    }
  }, [isFetchingOlderMessages, messages.length])

  useLayoutEffect(() => {
    restoreActiveScrollAnchor()
  }, [messages.length, restoreActiveScrollAnchor])

  useLayoutEffect(() => {
    updateTopVisibleMessage()
  }, [messages.length, updateTopVisibleMessage])

  useEffect(() => {
    const viewport = viewportRef.current

    if (!viewport || !activeScrollAnchor) return

    const handleViewportResize = () => {
      restoreActiveScrollAnchor()
    }

    const resizeObserver = new ResizeObserver(handleViewportResize)
    const mutationObserver = new MutationObserver(handleViewportResize)

    resizeObserver.observe(viewport)
    mutationObserver.observe(viewport, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
    })

    const animationFrame = window.requestAnimationFrame(() => {
      restoreActiveScrollAnchor()
    })

    scheduleAnchorRelease()

    return () => {
      window.cancelAnimationFrame(animationFrame)
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [activeScrollAnchor, restoreActiveScrollAnchor, scheduleAnchorRelease])

  const fetchOlderMessages = useCallback(async () => {
    const viewport = viewportRef.current

    if (
      !viewport ||
      !isRoomSyncFinished ||
      isFetchingOlderMessages ||
      !hasMoreOlderMessagesRef.current ||
      messages.length === 0 ||
      requestedCountRef.current >= messages.length
    ) {
      return
    }

    requestedCountRef.current = messages.length
    pendingScrollRestoreRef.current = getScrollAnchorSnapshot(
      viewport,
      messages.length,
    )
    setIsFetchingOlderMessages(true)

    try {
      const hasMore = await onFetchMoreMessages()
      if (hasMore === false) {
        hasMoreOlderMessagesRef.current = false
      }
    } finally {
      setIsFetchingOlderMessages(false)
    }
  }, [
    isFetchingOlderMessages,
    isRoomSyncFinished,
    messages.length,
    onFetchMoreMessages,
  ])

  const handleViewportScroll = useCallback(() => {
    const viewport = viewportRef.current

    updateTopVisibleMessage()
    setIsScrolling(true)

    if (scrollingTimeoutRef.current != null) {
      window.clearTimeout(scrollingTimeoutRef.current)
    }
    scrollingTimeoutRef.current = window.setTimeout(() => {
      setIsScrolling(false)
      scrollingTimeoutRef.current = null
    }, SCROLLING_IDLE_MS)

    if (!viewport || viewport.scrollTop > SCROLL_TOP_THRESHOLD) {
      return
    }

    void fetchOlderMessages()
  }, [fetchOlderMessages, updateTopVisibleMessage])

  useEffect(() => {
    const viewport = viewportRef.current

    if (!viewport) return
    if (viewport.scrollHeight > viewport.clientHeight) return

    void fetchOlderMessages()
  }, [fetchOlderMessages, messages.length])

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <ThreadPrimitive.Root className="relative flex flex-1 flex-col overflow-hidden bg-background">
        <AIChatFloatingDayMarker
          date={topVisibleDate}
          isScrolling={isScrolling}
          isFetchingOlderMessages={isFetchingOlderMessages}
        />
        <ThreadPrimitive.Viewport
          ref={viewportRef}
          data-testid="ai-chat-viewport"
          onScroll={handleViewportScroll}
          autoScroll={!isFetchingOlderMessages}
          className="flex flex-1 flex-col overflow-y-auto scrollbar-borderless"
        >
          {isFetchingOlderMessages && (
            <div className="sticky top-2 z-10 flex justify-center px-4">
              <div className="flex items-center gap-2 rounded-full border border-border/70 bg-background/95 px-3 py-1 text-xs font-medium text-muted-foreground shadow-sm backdrop-blur-sm">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {t("ai.chatView.loadingOlderMessages")}
              </div>
            </div>
          )}
          <div className="mx-auto w-full max-w-[800px]">
            <div className="flex-1" />
            {ftueEnabled && isEmptyChat ? (
              <AgentWelcome
                popular={popular}
                groups={groups}
                onSelectPrompt={handleSelectPrompt}
                isAnonymous={window.anonymousXMPP === true}
                english={englishFtue}
                polish={polishFtue}
              />
            ) : (
              <>
                <ThreadPrimitive.Messages>
                  {({ message }: { message: MessageState }) => (
                    <AIChatMessageRow message={message} />
                  )}
                </ThreadPrimitive.Messages>
                {ftueEnabled && showCapabilities && (
                  <div className="px-4 pb-4">
                    <AgentCapabilities
                      popular={popular}
                      groups={groups}
                      onSelectPrompt={handleSelectPrompt}
                      english={englishFtue}
                      polish={polishFtue}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </ThreadPrimitive.Viewport>
        {(showPromptPill || languageToggleAvailable) && (
          // Align the trigger with the composer below: same centered
          // max-w-[800px] container + matching horizontal padding as the
          // ChatInput wrapper, so the pill starts where the input box starts.
          <div className="px-2.5 pb-2 pt-1">
            <div className="mx-auto flex w-full max-w-[800px] items-center justify-between gap-2">
              {showPromptPill ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isAgentResponding}
                  className="gap-2 rounded-full text-primary"
                  onClick={() => setShowCapabilities((v) => !v)}
                >
                  <Sparkles className="h-4 w-4" />
                  {tFtue("ai.chatView.whatCanIAsk")}
                </Button>
              ) : (
                <span />
              )}
              {languageToggleAvailable && (
                <div
                  data-testid="agent-language-toggle"
                  className="flex shrink-0 items-center gap-0.5 rounded-full border border-border p-0.5"
                >
                  {(["pl", "en"] as const).map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      aria-pressed={english === (lang === "en")}
                      onClick={() => setEnglish(lang === "en")}
                      className={cn(
                        "rounded-full px-2.5 py-1 text-xs font-medium uppercase transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        english === (lang === "en")
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      {lang}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        <ThreadPrimitive.ScrollToBottom asChild>
          <Button
            variant="floating"
            size="floating"
            data-testid="scroll-to-bottom"
            className="absolute bottom-[10px] right-5 z-10 shadow-lg transition-opacity duration-300 disabled:pointer-events-none disabled:opacity-0"
          >
            <ChevronDown size={24} />
          </Button>
        </ThreadPrimitive.ScrollToBottom>
      </ThreadPrimitive.Root>
    </AssistantRuntimeProvider>
  )
}
